import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import {
  Sparkles, Diamond, Star, Phone, Users, Heart, MessageCircle,
  Zap, Edit2, Camera, BadgeCheck, Bookmark, Music, Wallet as WalletIcon, Shield, ChevronRight
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import AdminMessageCallout from "../components/AdminMessageCallout";
import MantraSettings from "../components/MantraSettings";
import { SiteNavigation } from "../components/navigation/SiteNavigation";

const FanDashboard = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ display_name: "", bio: "", favorite_genre: "" });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [profileRes, summaryRes] = await Promise.all([
        axios.get(`${API}/fan-profile/${user.user_id}`),
        axios.get(`${API}/fan-profile/me/summary`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setProfile(profileRes.data);
      setSummary(summaryRes.data);
      setEditForm({ display_name: profileRes.data.display_name || "", bio: profileRes.data.bio || "", favorite_genre: profileRes.data.favorite_genre || "" });
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const handleSaveProfile = async () => {
    try {
      const res = await axios.put(`${API}/fan-profile`, editForm, { headers: { Authorization: `Bearer ${token}` } });
      setProfile(res.data);
      setEditing(false);
      toast.success("Profile updated!");
    } catch (e) { toast.error("Failed to save profile"); }
  };

  if (loading) return <div className="min-h-screen bg-[#050810] flex items-center justify-center"><div className="spinner" /></div>;

  return (
    <div className="min-h-screen bg-[#050810] workspace-surface" data-testid="fan-dashboard-page">
      <header className="fixed top-0 left-0 right-0 z-50 glass-strong public-navigation-header">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/home" className="flex items-center gap-2">
            <img src="/logo.png" alt="StardomFans" className="w-10 h-10 object-contain" style={{ mixBlendMode: "lighten" }} />
          </Link>
          <SiteNavigation scope="fan" extraLinks={[{ path: "/todays-feed", label: "Feed" }]} />
        </div>
      </header>

      <main className="pt-24 pb-20 px-6">
        <div className="max-w-5xl mx-auto">
          <AdminMessageCallout />
          {/* Profile Card */}
          <div className="card p-8 mb-8 relative overflow-hidden" data-testid="fan-profile-card">
            <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-[#EC008C] via-[#FF6BC5] to-[#D14A85]" />
            <div className="flex flex-col md:flex-row items-start gap-6">
              <div className="w-20 h-20 rounded-2xl bg-[#1A2030] flex items-center justify-center overflow-hidden flex-shrink-0">
                {profile?.profile_image ? (
                  <img src={profile.profile_image} alt="" className="w-full h-full object-cover" />
                ) : (
                  <Users className="w-10 h-10 text-[#8A92A0]" />
                )}
              </div>
              <div className="flex-1">
                {editing ? (
                  <div className="space-y-3">
                    <Input value={editForm.display_name} onChange={(e) => setEditForm({...editForm, display_name: e.target.value})} className="bg-[#050810] border-[#1A2030] text-white" placeholder="Display name" data-testid="edit-name-input" />
                    <Textarea value={editForm.bio} onChange={(e) => setEditForm({...editForm, bio: e.target.value})} className="bg-[#050810] border-[#1A2030] text-white" placeholder="About you..." rows={2} maxLength={300} data-testid="edit-bio-input" />
                    <Input value={editForm.favorite_genre} onChange={(e) => setEditForm({...editForm, favorite_genre: e.target.value})} className="bg-[#050810] border-[#1A2030] text-white" placeholder="Favorite genre (e.g. Hip-Hop, R&B)" data-testid="edit-genre-input" />
                    <div className="flex gap-2">
                      <Button onClick={handleSaveProfile} className="bg-[#EC008C] text-[#000000] hover:bg-[#FF3399]" data-testid="save-profile-btn">Save</Button>
                      <Button onClick={() => setEditing(false)} variant="outline" className="border-[#1A2030] text-[#8A92A0]">Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-3 mb-2">
                      <h1 className="font-['Unbounded'] text-2xl font-bold text-white">{profile?.display_name || user?.name}</h1>
                      <button onClick={() => setEditing(true)} className="text-[#8A92A0] hover:text-[#EC008C] ml-auto" data-testid="edit-profile-btn"><Edit2 className="w-4 h-4" /></button>
                    </div>
                    {profile?.bio && <p className="text-[#8A92A0] text-sm mb-2">{profile.bio}</p>}
                    {profile?.favorite_genre && <span className="text-xs text-[#EC008C] bg-[#EC008C]/10 px-2 py-1 rounded-full">{profile.favorite_genre}</span>}
                    {/* Badges */}
                    {profile?.badges?.length > 0 && (
                      <div className="flex gap-2 mt-3">
                        {profile.badges.map((badge) => (
                          <span key={badge} className="px-2 py-1 rounded-full text-[10px] font-bold bg-[#D14A85]/10 text-[#D14A85] border border-[#D14A85]/30">{badge}</span>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mt-6 pt-6 border-t border-[#1A2030]">
              {[
                { label: "Following", value: profile?.stats?.following || 0, icon: Heart },
                { label: "Collectibles", value: profile?.stats?.collectibles || 0, icon: Diamond },
                { label: "Experiences", value: profile?.stats?.experiences || 0, icon: Star },
                { label: "Messages", value: profile?.stats?.interactions || 0, icon: MessageCircle },
                { label: "Posts", value: profile?.stats?.posts || 0, icon: Zap }
              ].map((stat) => (
                <div key={stat.label} className="text-center">
                  <stat.icon className="w-4 h-4 text-[#8A92A0] mx-auto mb-1" />
                  <p className="text-white font-bold text-lg">{stat.value}</p>
                  <p className="text-[#4A5260] text-xs">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Nav */}
          <nav className="dashboard-quick-groups" aria-label="Your fan dashboard" data-testid="fan-grouped-navigation">
            {[
              { id: "library", label: "Library & experiences", items: [
                { label: "My Collectibles", href: "/my-collectibles", icon: Diamond, color: "#A78BFA" },
                { label: "My Experiences", href: "/my-experiences", icon: Star, color: "#FF3B3B" },
              ] },
              { id: "connect", label: "Connect & discover", items: [
                { label: "AI Messages", href: "/my-ai-messages", icon: Sparkles, color: "#60A5FA" },
                { label: "Bookmarks", href: "/community", icon: Bookmark, color: "#A1A1AA" },
              ] },
              { id: "account", label: "Wallet & account", items: [
                { label: "Fan Wallet", href: "/wallet", icon: WalletIcon, color: "#32D583" },
                { label: "Sessions", href: "/security/sessions", icon: Shield, color: "#60A5FA" },
              ] },
            ].map((group) => (
              <section className="dashboard-quick-group" key={group.id} data-testid={`fan-navigation-group-${group.id}`}>
                <h2 data-testid={`fan-navigation-heading-${group.id}`}>{group.label}</h2>
                <div className="dashboard-quick-group-links">
                  {group.items.map(item => <Link key={item.label} to={item.href} className="fan-shortcut-link" data-testid={`nav-${item.label.toLowerCase().replace(/ /g, '-')}`}>
                    <item.icon className="w-4 h-4 shrink-0" style={{ color: item.color }} aria-hidden="true" />
                    <span>{item.label}</span><ChevronRight className="w-4 h-4 ml-auto shrink-0" aria-hidden="true" />
                  </Link>)}
                </div>
              </section>
            ))}
          </nav>

          {/* Mantra Suite */}
          <div className="mb-8">
            <MantraSettings token={token} compact showTrillionaire={false} />
          </div>

          {/* Activity Sections */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Recent Collectibles */}
            <div className="card p-5">
              <h3 className="font-semibold text-white mb-4 flex items-center gap-2"><Diamond className="w-4 h-4 text-[#EC008C]" /> Recent Collectibles</h3>
              {summary?.collectibles?.length > 0 ? (
                <div className="space-y-3">
                  {summary.collectibles.slice(0, 4).map((c, i) => (
                    <div key={i} className="flex items-center gap-3 text-sm">
                      <div className="w-8 h-8 rounded-lg bg-[#EC008C]/10 flex items-center justify-center"><Diamond className="w-4 h-4 text-[#EC008C]" /></div>
                      <span className="text-white flex-1 truncate">{c.title}</span>
                      <span className="text-[#4A5260] text-xs">#{c.edition_number}</span>
                    </div>
                  ))}
                </div>
              ) : <p className="text-[#4A5260] text-sm">No collectibles yet</p>}
            </div>

            {/* Recent Experiences */}
            <div className="card p-5">
              <h3 className="font-semibold text-white mb-4 flex items-center gap-2"><Star className="w-4 h-4 text-[#D14A85]" /> Recent Experiences</h3>
              {summary?.experiences?.length > 0 ? (
                <div className="space-y-3">
                  {summary.experiences.slice(0, 4).map((e, i) => (
                    <div key={i} className="flex items-center gap-3 text-sm">
                      <div className="w-8 h-8 rounded-lg bg-[#D14A85]/10 flex items-center justify-center"><Star className="w-4 h-4 text-[#D14A85]" /></div>
                      <span className="text-white flex-1 truncate">{e.title}</span>
                      <span className="text-[#4A5260] text-xs">{new Date(e.booked_at).toLocaleDateString()}</span>
                    </div>
                  ))}
                </div>
              ) : <p className="text-[#4A5260] text-sm">No experiences booked yet</p>}
            </div>

            {/* Recent Interactions */}
            <div className="card p-5">
              <h3 className="font-semibold text-white mb-4 flex items-center gap-2"><MessageCircle className="w-4 h-4 text-[#FF6BC5]" /> Paid Messages</h3>
              {summary?.interactions?.length > 0 ? (
                <div className="space-y-3">
                  {summary.interactions.slice(0, 4).map((m, i) => (
                    <div key={i} className="flex items-start gap-3 text-sm">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${m.highlight ? 'bg-[#EC008C]/10' : 'bg-[#FF6BC5]/10'}`}>
                        <MessageCircle className={`w-4 h-4 ${m.highlight ? 'text-[#EC008C]' : 'text-[#FF6BC5]'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white truncate">{m.text}</p>
                        {m.artist_reply && <p className="text-[#EC008C] text-xs mt-1 truncate">Reply: {m.artist_reply}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              ) : <p className="text-[#4A5260] text-sm">No paid messages yet</p>}
            </div>

            {/* AI Messages */}
            <div className="card p-5">
              <h3 className="font-semibold text-white mb-4 flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#EC008C]" /> AI Messages</h3>
              {summary?.ai_messages?.length > 0 ? (
                <div className="space-y-3">
                  {summary.ai_messages.slice(0, 4).map((m, i) => (
                    <div key={i} className="flex items-center gap-3 text-sm">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#D14A85]/10 to-[#EC008C]/10 flex items-center justify-center"><Sparkles className="w-4 h-4 text-[#EC008C]" /></div>
                      <span className="text-white flex-1 truncate">From {m.artist_name}</span>
                    </div>
                  ))}
                </div>
              ) : <p className="text-[#4A5260] text-sm">No AI messages yet</p>}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default FanDashboard;
